import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  TrainingRecord,
  TrainingTableSortColumn,
  TrainingCompletionStatus,
  ViewMode,
} from '../TrainingList/types';
import { SortDirection } from '../../../Types/SortDirection';
import { trainingResultsPerPage } from '../types/constants';
import { ExtendedUser } from '../../../Types';

export interface UseTrainingPaginationProps {
  trainings: TrainingRecord[];
  sortColumn: TrainingTableSortColumn;
  sortDirection: SortDirection;
  selectedCSA?: ExtendedUser;
  viewMode?: ViewMode;
}

export interface UseTrainingPaginationReturn {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  paginatedData: TrainingRecord[];
  sortedTrainings: TrainingRecord[];
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
}

/**
 * Custom hook for handling Training-specific pagination and sorting logic
 * Provides memoized pagination calculations and state management for training data
 */
export function useTrainingPagination({
  trainings,
  sortColumn,
  sortDirection,
  selectedCSA,
  viewMode,
}: UseTrainingPaginationProps): UseTrainingPaginationReturn {
  const [currentPage, setCurrentPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(trainingResultsPerPage[1]);

  // Priority mapping for completion status sorting (from original component)
  const getStatusPriority = (status: TrainingCompletionStatus): number => {
    switch (status) {
      case TrainingCompletionStatus.OVERDUE:
        return 1;
      case TrainingCompletionStatus.NOT_STARTED:
        return 2;
      case TrainingCompletionStatus.IN_PROGRESS:
        return 3;
      case TrainingCompletionStatus.COMPLETED:
        return 4;
      default:
        return 5; // Unknown statuses go to the end
    }
  };

  // Memoized sorting - prevents unnecessary re-sorting (using original algorithm)
  const sortedTrainings = useMemo(() => {
    if (!trainings || !Array.isArray(trainings)) {
      return [];
    }

    return [...trainings].sort((a, b) => {
      if (sortColumn === TrainingTableSortColumn.COMPLETION_STATUS) {
        // Custom priority sorting: Overdue > Not Started > In Progress > Completed
        const priorityA = getStatusPriority(a.completionStatus);
        const priorityB = getStatusPriority(b.completionStatus);

        if (priorityA !== priorityB) {
          return sortDirection === SortDirection.ASCENDING
            ? priorityA - priorityB
            : priorityB - priorityA;
        }
        return 0;
      }

      // Default sorting for other columns
      if (a[sortColumn] < b[sortColumn]) {
        return sortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (a[sortColumn] > b[sortColumn]) {
        return sortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });
  }, [trainings, sortColumn, sortDirection]);

  // Memoized pagination calculations
  const paginationData = useMemo(() => {
    const totalItems = sortedTrainings.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedTrainings.slice(startIndex, endIndex);

    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      paginatedData,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [sortedTrainings, currentPage, pageSize]);

  // Reset to first page when selectedCSA or view mode changes
  useEffect(() => {
    if (selectedCSA || viewMode) {
      setCurrentPageState(1);
      setPageSizeState(trainingResultsPerPage[1]);
    }
  }, [selectedCSA, viewMode]);

  // Reset to first page when data changes significantly
  const dataLength = trainings.length;
  useMemo(() => {
    if (
      currentPage > paginationData.totalPages &&
      paginationData.totalPages > 0
    ) {
      setCurrentPageState(1);
    }
  }, [dataLength, currentPage, paginationData.totalPages]);

  // Memoized setters to prevent unnecessary re-renders
  const setCurrentPage = useCallback(
    (page: number) => {
      const clampedPage = Math.max(
        1,
        Math.min(page, paginationData.totalPages || 1)
      );
      setCurrentPageState(clampedPage);
    },
    [paginationData.totalPages]
  );

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPageState(1); // Reset to first page when page size changes
  }, []);

  const goToNextPage = useCallback(() => {
    if (paginationData.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, paginationData.hasNextPage, setCurrentPage]);

  const goToPreviousPage = useCallback(() => {
    if (paginationData.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage, paginationData.hasPreviousPage, setCurrentPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  const goToLastPage = useCallback(() => {
    setCurrentPage(paginationData.totalPages);
  }, [paginationData.totalPages, setCurrentPage]);

  return {
    currentPage,
    pageSize,
    totalPages: paginationData.totalPages,
    totalItems: paginationData.totalItems,
    paginatedData: paginationData.paginatedData,
    sortedTrainings,
    startIndex: paginationData.startIndex,
    endIndex: paginationData.endIndex,
    hasNextPage: paginationData.hasNextPage,
    hasPreviousPage: paginationData.hasPreviousPage,
    setCurrentPage,
    setPageSize,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
  };
}
